/*
 * WHERE A NAME MAY BE WRITTEN — the two rules that decide it, and nothing else.
 *
 * Ported from `reference/universe-v8/engine.js` — `tfree` (551–557) and `blocked` (558–562).
 * `measureBars` (540–550) is deliberately NOT ported: it reads the DOM to find what the chrome
 * covers, and this app already does that in `MapCanvas.chromeBands`, which measures the real
 * elements off `[data-map-chrome]`. Porting a second DOM reader would be two answers to one
 * question. What is ported is the arithmetic that consumes the answer.
 *
 * GREEDY, AND THAT IS THE POINT. Labels are offered in order of what deserves one most, and each
 * takes the first place it fits. There is no global optimisation and there should not be: a
 * placement that shuffles when a neighbour arrives makes the map twitch, and the map is streamed,
 * so neighbours arrive constantly.
 */

/** A rectangle in screen pixels. */
export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Whether two rectangles touch at all. Their test, and the only geometry here. */
export function overlaps(a: Box, b: Box): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

/**
 * The space labels are competing for, over one frame.
 *
 * `chrome` is where the interface covers the map — measured from the real elements, never guessed,
 * because a name drawn under the search box is ink spent on something nobody can read AND a place
 * taken from a figure that could have been named there.
 */
export class LabelSpace {
  private readonly chrome: readonly Box[];
  private readonly taken: Box[] = [];

  constructor(chrome: readonly Box[] = []) {
    this.chrome = chrome;
  }

  /** Whether the interface covers this rectangle. Their `blocked`. */
  blocked(box: Box): boolean {
    for (const r of this.chrome) if (overlaps(box, r)) return true;
    return false;
  }

  /**
   * Claim a rectangle if it is free, and report whether it was. Their `tfree`.
   *
   * The claim happens INSIDE the test, which is what makes the placement greedy: asking is taking.
   * Two callers cannot both be told yes for the same space.
   *
   * `keep` is what gets RESERVED when `box` is what gets TESTED, and they are not always the same
   * rectangle: the reader chips test their exact box and then reserve it with three pixels of air
   * on every side, so two chips never end up touching even where each would have fitted. Left off,
   * a claim reserves exactly what it asked about, which is what every other caller wants.
   */
  claim(box: Box, keep: Box = box): boolean {
    for (const r of this.taken) if (overlaps(box, r)) return false;
    this.taken.push(keep);
    return true;
  }

  /** How many labels have been placed — what a cap is counted against. */
  get count(): number {
    return this.taken.length;
  }
}
